import React from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { MarketingLayout, Section, Overline } from "@/marketing/MarketingLayout";
import { HOME, PLAN } from "@/marketing/content";
import {
  ArrowRight, CheckCircle2, FileText, MessagesSquare, Briefcase, Star, ShieldCheck, Zap,
} from "lucide-react";

const SVC_ICONS = [FileText, MessagesSquare, Briefcase];

export default function MarketingHome() {
  const navigate = useNavigate();
  const go = () => navigate("/register");

  return (
    <MarketingLayout>
      {/* Hero */}
      <div className="relative overflow-hidden">
        <div className="absolute top-[-120px] right-[-120px] w-[600px] h-[600px] bg-primary/10 rounded-full blur-[120px] -z-0" />
        <Section className="relative z-10 pt-20 pb-16 lg:pt-28">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-border bg-card mb-6 fade-in-up">
            <Zap className="w-3.5 h-3.5 text-primary" />
            <span className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">Career Launchpad for Freshers</span>
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-none mb-6 fade-in-up max-w-3xl">
            {HOME.hero.headline}<br />
            <span className="text-primary">{HOME.hero.headlineAccent}</span>
          </h1>
          <p className="text-base lg:text-lg text-muted-foreground max-w-2xl mb-8 fade-in-up leading-relaxed">{HOME.hero.subheadline}</p>
          <div className="flex flex-wrap gap-3 fade-in-up mb-10">
            <Button size="lg" onClick={go} className="rounded-md gap-2 h-12 px-6" data-testid="hero-cta-register">
              {PLAN.cta} <ArrowRight className="w-4 h-4" />
            </Button>
            <Button size="lg" variant="outline" onClick={() => navigate("/services")} className="rounded-md h-12 px-6" data-testid="hero-cta-services">
              Explore Services
            </Button>
          </div>
          <div className="flex flex-wrap gap-x-6 gap-y-2">
            {HOME.trustBadges.map((b) => (
              <div key={b} className="flex items-center gap-2 text-sm text-muted-foreground" data-testid="trust-badge">
                <CheckCircle2 className="w-4 h-4 text-success" /> {b}
              </div>
            ))}
          </div>
        </Section>
      </div>

      {/* Stats */}
      <Section className="pb-8">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {HOME.stats.map((s) => (
            <div key={s.label} className="p-6 rounded-md border border-border bg-card" data-testid="home-stat">
              <div className="font-mono text-3xl lg:text-4xl font-black text-primary">{s.value}</div>
              <div className="text-sm text-muted-foreground mt-1">{s.label}</div>
            </div>
          ))}
        </div>
      </Section>

      {/* Services */}
      <Section className="pt-16">
        <Overline>What we do</Overline>
        <h2 className="font-heading text-3xl lg:text-4xl font-bold tracking-tight mb-10">Three steps to your first job</h2>
        <div className="grid md:grid-cols-3 gap-4">
          {HOME.services.map((svc, i) => {
            const Icon = SVC_ICONS[i];
            return (
              <div key={svc.title} className="p-6 rounded-md border border-border bg-card hover:-translate-y-1 transition-transform" data-testid="home-service">
                <div className="w-11 h-11 rounded-md bg-primary/10 flex items-center justify-center mb-4"><Icon className="w-5 h-5 text-primary" /></div>
                <span className="text-xs font-mono uppercase tracking-wider text-primary">{svc.tag}</span>
                <h3 className="font-heading font-bold text-xl mt-1 mb-2">{svc.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{svc.description}</p>
              </div>
            );
          })}
        </div>
      </Section>

      {/* How it works */}
      <Section className="pt-20">
        <Overline>How it works</Overline>
        <h2 className="font-heading text-3xl lg:text-4xl font-bold tracking-tight mb-2">{HOME.howItWorks.heading}</h2>
        <p className="text-muted-foreground mb-10 max-w-2xl">{HOME.howItWorks.subheading}</p>
        <div className="grid md:grid-cols-3 gap-4">
          {HOME.howItWorks.steps.map((s) => (
            <div key={s.number} className="p-6 rounded-md border border-border bg-card" data-testid="home-step">
              <div className="font-mono text-4xl font-black text-primary/30 mb-3">{s.number}</div>
              <h3 className="font-heading font-bold text-lg mb-2">{s.title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{s.description}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* Pricing */}
      <Section className="pt-20">
        <div className="rounded-md border border-primary/40 bg-card overflow-hidden">
          <div className="grid lg:grid-cols-2">
            <div className="p-8 lg:p-10 border-b lg:border-b-0 lg:border-r border-border">
              <Overline>Membership</Overline>
              <h2 className="font-heading text-3xl font-bold tracking-tight mb-1">{PLAN.name}</h2>
              <p className="text-muted-foreground mb-6">{PLAN.tagline}</p>
              <div className="flex items-end gap-2 mb-6">
                <span className="font-mono text-5xl font-black">{PLAN.price}</span>
                <span className="text-muted-foreground mb-1">/ {PLAN.period}</span>
              </div>
              <Button size="lg" onClick={go} className="rounded-md gap-2 h-12 px-6 w-full sm:w-auto" data-testid="pricing-cta">
                {PLAN.cta} <ArrowRight className="w-4 h-4" />
              </Button>
              <div className="flex items-start gap-2 mt-5 text-sm text-muted-foreground">
                <ShieldCheck className="w-4 h-4 text-success shrink-0 mt-0.5" /> {PLAN.guarantee}
              </div>
            </div>
            <div className="p-8 lg:p-10">
              <p className="font-heading font-bold mb-4">Everything included</p>
              <ul className="space-y-3">
                {PLAN.features.map((f) => (
                  <li key={f} className="flex items-start gap-2.5 text-sm" data-testid="plan-feature">
                    <CheckCircle2 className="w-4 h-4 text-success shrink-0 mt-0.5" /><span className="text-muted-foreground">{f}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </Section>

      {/* Testimonials */}
      <Section className="pt-20">
        <Overline>Success stories</Overline>
        <h2 className="font-heading text-3xl lg:text-4xl font-bold tracking-tight mb-2">{HOME.testimonials.heading}</h2>
        <p className="text-muted-foreground mb-10 max-w-2xl">{HOME.testimonials.subheading}</p>
        <div className="grid md:grid-cols-3 gap-4">
          {HOME.testimonials.items.map((t) => (
            <div key={t.name} className="p-6 rounded-md border border-border bg-card flex flex-col" data-testid="home-testimonial">
              <div className="flex gap-0.5 mb-3">{[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 fill-primary text-primary" />)}</div>
              <p className="text-sm leading-relaxed mb-4 flex-1">"{t.quote}"</p>
              <div className="pt-4 border-t border-border">
                <div className="font-semibold text-sm">{t.name}</div>
                <div className="text-xs text-muted-foreground">{t.role} · {t.company} · {t.location}</div>
                <div className="text-xs font-mono text-success mt-1">{t.package}</div>
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* Final CTA */}
      <Section className="pt-20">
        <div className="rounded-md bg-primary text-primary-foreground p-10 lg:p-14 text-center relative overflow-hidden">
          <h2 className="font-heading text-3xl lg:text-4xl font-black tracking-tight mb-3">{HOME.finalCta.heading}</h2>
          <p className="opacity-90 max-w-2xl mx-auto mb-7">{HOME.finalCta.subheading}</p>
          <Button size="lg" variant="secondary" onClick={go} className="rounded-md gap-2 h-12 px-6" data-testid="final-cta">
            {PLAN.cta} <ArrowRight className="w-4 h-4" />
          </Button>
        </div>
      </Section>
    </MarketingLayout>
  );
}
